import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { fetchMyCommandes } from '../api/commandeService';
import { Eye, ChevronLeft, ChevronRight } from 'lucide-react';

const OrdersHistoryScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalRef, setModalRef] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;

useEffect(() => {
  if (!user) return navigate('/signin');
  const loadOrders = async () => {
    const data = await fetchMyCommandes();
    setOrders((data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    setLoading(false);
  };
  loadOrders();
}, [user]);


  const closeModal = () => setModalRef(null);

  const filteredOrders = orders.filter((order) => {
    const matchReference = order.reference?.toLowerCase().includes(search.toLowerCase());
    const matchTotal = order.totalPrice?.toString().includes(search);
    const matchProduct = order.items.some(item => item.product?.name?.toLowerCase().includes(search.toLowerCase()));
    return matchReference || matchTotal || matchProduct;
  });

  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const currentOrders = filteredOrders.slice(startIndex, startIndex + ordersPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <img src="/assets/loading.gif" alt="Chargement..." className="w-20 h-20" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-28 p-6">
      <h1 className="text-3xl font-bold text-paragaia mb-8 text-center">Historique des Commandes</h1>

      <div className="max-w-3xl mx-auto mb-6">
        <input
          type="text"
          placeholder="Rechercher par produit, référence ou total"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-paragaia"
        />
      </div>

      {currentOrders.length === 0 ? (
        <div className="text-center text-gray-500 text-lg">Aucune commande trouvée.</div>
      ) : (
        <div className="space-y-8 max-w-3xl mx-auto">
          {currentOrders.map((order) => {
            const totalQuantity = order.items.reduce((acc, item) => acc + item.quantity, 0);
            const estimatedPrice = totalQuantity > 0 ? order.subTotal / totalQuantity : null;

            return (
              <div
                key={order._id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-6 border-t-4 border-paragaiaLight"
              >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="text-xl font-semibold text-gray-800">
                        Commande #{order.reference?.slice(0, 12)}...
                      </p>
                      <Eye
                        size={18}
                        className="cursor-pointer text-paragaia hover:text-paragaiaLight"
                        onClick={() => setModalRef(order.reference)}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="mt-3 md:mt-0">
                    <span className={`text-xs font-medium px-3 py-1 rounded-full
                      ${
                        order.status === 'confirmed'
                          ? 'bg-green-100 text-green-700'
                          : order.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                      {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 border-t border-gray-100 pt-4">
                  {order.items.map((item) => (
                    <div key={item._id} className="flex justify-between items-center text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        {item.product?.image && (
                          <img
                            src={item.product.image}
                            alt={item.product.name}
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <span>{item.product?.name || 'Produit'}</span>
                      </div>
                      <span>
                        {item.quantity} ×{' '}
                        {estimatedPrice !== null
                          ? `${estimatedPrice.toFixed(2)} TND`
                          : '?? TND'}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 text-right font-bold text-lg text-paragaia">
                  Total : {(order.totalPrice ?? 0).toFixed(2)} TND
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-center items-center mt-10 space-x-2">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          className="px-3 py-1 rounded border text-paragaia border-paragaia hover:bg-paragaia hover:text-white"
          disabled={currentPage === 1}
        >
          <ChevronLeft size={16} />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-3 py-1 rounded border ${
              page === currentPage ? 'bg-paragaia text-white' : 'text-paragaia border-paragaia'
            }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          className="px-3 py-1 rounded border text-paragaia border-paragaia hover:bg-paragaia hover:text-white"
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {modalRef && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Référence complète</h2>
            <p className="text-paragaia font-mono break-all">{modalRef}</p>
            <button
              onClick={closeModal}
              className="mt-6 bg-paragaia text-white px-4 py-2 rounded-lg hover:bg-paragaiaLight transition"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersHistoryScreen;
